import { useEffect, useMemo, useState } from "react";
import { Globe } from "lucide-react";
import BottomSheet from "../common/BottomSheet";
import { Flag } from "./lawyerFlag";
import {
  SearchIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CheckmarkIcon,
} from "./v2/icons";

// ============================================================================
// Mobile filter bottom-sheet — presentation rebuilt 1:1 from handoff board 1l
// (filter sheet, country expanded): accordion sections (13px radius, expanded
// header on #F8FAFD with chevron-up), option rows with 19×19 checkboxes
// (checked #2283FF + white check w3.5), country search input, "Clear all" and
// the full-width "Show N lawyers" apply CTA.
//
// The same 4 client-side filters; filtering stays live, "Show N lawyers"
// just closes (exactly what the old "Show results (N)" footer did). The
// COUNTRY section is true multi-select (follow-up pack A2, board 1l
// checkboxes): each tap toggles that country in/out of the selection via
// toggleFilter, several rows can be checked at once, and one checked row
// behaves exactly like the old single-select (degenerate case). The other
// sections stay single-select through setFilter: one row checked, tapping
// another moves the selection, tapping the checked row clears it.
// Built on the shared BottomSheet (grabber/backdrop/swipe/X per platform
// contract); "Clear all" sits at the top of the body because the shared
// header slot is title+X (documented deviation, parity file section G).
// `initialSection` (presentational) opens the sheet with a section expanded —
// board 1k's filter pills open their own section, country by default.
// ============================================================================

const SECTION_DEFS = [
  { key: "jurisdiction", facet: "jurisdictions", searchable: true, withFlags: true, multi: true },
  { key: "specialization", facet: "specializations" },
  { key: "language", facet: "languages" },
  // "City" has no accordion drawn on board 1l (cut off) — kept for function
  // parity (F-005 city filter), styled like the other sections.
  { key: "city", facet: "cities", searchable: true },
];

const LawyerFilterSheet = ({
  open,
  onClose,
  facets,
  filters,
  setFilter,
  toggleFilter,
  resetFilters,
  jurisdictionFlags = {},
  resultCount = 0,
  initialSection = "jurisdiction",
  t = (s) => s,
}) => {
  const [expanded, setExpanded] = useState(initialSection);
  const [query, setQuery] = useState("");

  // Re-open with the requested section expanded; reset the inline search.
  useEffect(() => {
    if (open) {
      setExpanded(initialSection || "jurisdiction");
      setQuery("");
    }
  }, [open, initialSection]);

  const sectionLabel = (key) =>
    ({
      // Plain text: the globe renders as the Lucide icon next to the label
      // (T050-1), never as an emoji character — and this string doubles as the
      // section's search placeholder, which must stay glyph-free.
      jurisdiction: t("Country / Jurisdiction"),
      specialization: t("Specialization"),
      language: t("Language"),
      city: t("City"),
    }[key]);

  const flagFor = (v) => jurisdictionFlags[v] || {};

  const optionsFor = (def) => {
    const options = facets[def.facet] || [];
    if (!def.searchable || expanded !== def.key) return options;
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => {
      const label = (def.withFlags && flagFor(o).label) || o;
      return (
        o.toLowerCase().includes(q) || label.toLowerCase().includes(q)
      );
    });
  };

  const sections = useMemo(() => SECTION_DEFS, []);

  return (
    <BottomSheet open={open} onClose={onClose} title={t("Filters")}>
      <div className="pb-2">
        {/* "Clear all" — board 1l places it in the sheet header; the shared
            BottomSheet header is title+X, so it sits right under it. */}
        <div className="flex justify-end mb-2">
          <button
            type="button"
            onClick={resetFilters}
            className="text-[12.5px] text-[#2283FF] font-semibold"
          >
            {t("Clear all")}
          </button>
        </div>

        {sections.map((def) => {
          const isOpen = expanded === def.key;
          const selected = filters[def.key];
          const selectedList = def.multi
            ? Array.isArray(selected)
              ? selected
              : []
            : null;
          const options = optionsFor(def);
          return (
            <div
              key={def.key}
              className="border border-[#E6EAF2] rounded-[13px] overflow-hidden mb-2.5"
            >
              {/* Section header: expanded on #F8FAFD w/ chevron-up; collapsed
                  plain row w/ chevron-down (board 1l) */}
              <button
                type="button"
                onClick={() => {
                  setExpanded(isOpen ? null : def.key);
                  setQuery("");
                }}
                className={`w-full flex items-center justify-between px-3.5 py-3 text-left ${
                  isOpen
                    ? "bg-[#F8FAFD] border-b border-[#E6EAF2] text-[13.5px] font-bold text-[#101828]"
                    : "bg-white text-[13.5px] font-semibold text-[#344054]"
                }`}
              >
                <span className="inline-flex items-center gap-1.5">
                  {def.key === "jurisdiction" && (
                    <Globe size={13} className="flex-none" />
                  )}
                  {sectionLabel(def.key)}
                </span>
                {isOpen ? (
                  <ChevronUpIcon size={12} />
                ) : (
                  <ChevronDownIcon size={12} className="text-[#98A2B3]" />
                )}
              </button>

              {isOpen && (
                <div className="px-3.5 py-2.5 flex flex-col gap-0.5">
                  {def.searchable && (
                    <div className="flex items-center gap-2 border border-[#DDE3EC] rounded-[9px] px-[11px] py-2 text-[12.5px] text-[#98A2B3] mb-2">
                      <SearchIcon size={12} className="text-[#98A2B3]" />
                      <input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder={
                          def.key === "jurisdiction"
                            ? t("Search countries")
                            : sectionLabel(def.key)
                        }
                        className="flex-1 min-w-0 text-[12.5px] text-[#101828] placeholder:text-[#98A2B3] focus:outline-none"
                      />
                    </div>
                  )}

                  <div className="max-h-[240px] overflow-y-auto flex flex-col gap-0.5">
                    {options.length === 0 ? (
                      <div className="py-[9px] px-0.5 text-[13.5px] text-[#98A2B3]">
                        {t("No results")}
                      </div>
                    ) : (
                      options.map((opt) => {
                        const isSelected = def.multi
                          ? selectedList.includes(opt)
                          : opt === selected;
                        const label =
                          (def.withFlags && flagFor(opt).label) || opt;
                        return (
                          <button
                            key={opt}
                            type="button"
                            aria-pressed={isSelected}
                            onClick={() =>
                              def.multi
                                ? toggleFilter(def.key, opt)
                                : setFilter(def.key, isSelected ? "" : opt)
                            }
                            className="flex items-center justify-between py-[9px] px-0.5 text-left"
                          >
                            <span
                              className={`inline-flex items-center gap-2 text-[13.5px] ${
                                isSelected
                                  ? "font-semibold text-[#101828]"
                                  : "text-[#344054]"
                              }`}
                            >
                              {def.withFlags && flagFor(opt).code && (
                                <Flag {...flagFor(opt)} width={18} height={13} />
                              )}
                              {label}
                            </span>
                            {isSelected ? (
                              <span className="w-[19px] h-[19px] rounded-[6px] bg-[#2283FF] inline-flex items-center justify-center flex-none">
                                <CheckmarkIcon
                                  size={11}
                                  strokeWidth={3.5}
                                  className="text-white"
                                />
                              </span>
                            ) : (
                              <span className="w-[19px] h-[19px] rounded-[6px] border-[1.5px] border-[#D5DBE7] flex-none" />
                            )}
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* Apply — filtering is live; this just closes (existing behavior). */}
        <button
          type="button"
          onClick={onClose}
          className="w-full flex items-center justify-center bg-[#2283FF] active:bg-[#1570E8] text-white rounded-[11px] py-[13px] font-semibold text-[14px] mt-1.5"
        >
          {t("Show")} {resultCount} {t(resultCount === 1 ? "lawyer" : "lawyers")}
        </button>
      </div>
    </BottomSheet>
  );
};

export default LawyerFilterSheet;
